import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../redux/store';
import { vars } from '../../../Theme';
import AppTooltip from '../../General/AppTooltip/AppTooltip';
import { mdiClose, mdiArrowLeft } from '@mdi/js';
import Icon from '@mdi/react';
import {
    Button,
    ClickAwayListener,
    Drawer,
    DrawerProps,
    Stack,
    SxProps,
    Typography,
} from '@mui/material';
import { resetAdvancedSearchData } from '../../../redux/project/documents/sliceProjectAdvancedSearch';
import ProjectAreaIndicator from './ProjectAreaIndicator';
import SelectedDocumentsIndicator from './SelectedDocumentsIndicator';
import ModalResultTable from '../../General/ModalTable/ModalResultTable';
import { ProjectSidepanel, setActiveSidepanel } from '../../../redux/project/sliceProjectSidepanel';
import AppSidepanel from '../../General/Sidepanels/AppSidepanel';
import { fullSize } from '../../../Constants';

/** Base button styles. */
const buttonSx: SxProps = {
    fontFamily: 'inherit',
    textTransform: 'none',
    alignItems: 'center',
    fontSize: 'inherit',
    color: 'inherit',
    borderRadius: 2,
    minWidth: 24,
    p: 1,
};

const drawerProps: DrawerProps = {
    PaperProps: { style: { position: 'absolute' }, elevation: 3 },
    variant: 'permanent',
    anchor: 'right',
};

/**
 * Special view that shows information on the selected documents in a project.
 */
export default ({ setShowSelectedDocs }: { setShowSelectedDocs: (t: boolean) => void }) => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Access relevant state variables from the redux store. */
    const {
        data,
        areaSearchText,
        loading: isAdvancedSearchResultsLoading,
    } = useSelector((store: IAppReduxStore) => store.projectAdvancedSearch);
    const openAppSidepanel = useSelector((store: IAppReduxStore) => store.projectSidepanel.active);

    const handleClick = () => {
        dispatch(resetAdvancedSearchData());
    };

    return (
        <>
            <Stack
                direction='row'
                justifyContent='space-between'
                sx={{
                    alignItems: 'center',
                    height: 48,
                    px: 1,
                    pt: 1,
                    borderBottom: '1px solid rgba(0,0,0,0.12)',
                }}
            >
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <AppTooltip title={t('back')} placement='top-start'>
                        <Button sx={buttonSx} onClick={handleClick}>
                            <Icon path={mdiArrowLeft} size={1} color={vars.font} />
                        </Button>
                    </AppTooltip>
                    {!isAdvancedSearchResultsLoading && (
                        <Typography variant='h5' display='flex'>
                            {t('searchResults', { searchRequest: areaSearchText })}
                            <ProjectAreaIndicator />
                            &nbsp;{`(${data.length})`}
                        </Typography>
                    )}
                </Stack>

                <AppTooltip title={t('hide')} placement='top-start'>
                    <Button sx={buttonSx} onClick={handleClick}>
                        <Icon path={mdiClose} size={1} color={vars.font} />
                    </Button>
                </AppTooltip>
            </Stack>
            <Stack
                direction='row'
                sx={{
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <Stack
                    direction='row'
                    sx={{ ...fullSize, pl: 2, position: 'relative', overflow: 'hidden' }}
                >
                    <Stack
                        sx={{
                            overflow: 'auto',
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <ModalResultTable
                            documents={data}
                            checkboxSelection={true}
                            tableFor={'searchResults'}
                        />
                        <SelectedDocumentsIndicator onClick={() => setShowSelectedDocs(true)} />
                    </Stack>
                    <ClickAwayListener
                        mouseEvent='onMouseDown'
                        touchEvent='onTouchStart'
                        onClickAway={() => dispatch(setActiveSidepanel(ProjectSidepanel.None))}
                    >
                        <Drawer {...drawerProps} open={openAppSidepanel !== ProjectSidepanel.None}>
                            <AppSidepanel />
                        </Drawer>
                    </ClickAwayListener>
                </Stack>
            </Stack>
        </>
    );
};
